import React, { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerTechnicianTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { GETTechnicians } from '../../services/technician';
import { ITechnician } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import {
  TechnicianKycStatus,
  TechnicianStatus,
  TechnicianType,
} from '../../utils/entities/type';
import { AddEditTechnicianModal } from '../../components/ui/add/technicianModal';
import { ACD_technicianlist } from '../../components/ui/list/ACD_technicianlist';
import { TechnicianFilter } from '../../components/ui/filters/technician';

export interface TechnicianFilterData {
  type: TechnicianType | string;
  position: string;
  status: TechnicianStatus | string;
  kycStatus: TechnicianKycStatus | string;
  excludePosition: string;
}

const ACD_technician = () => {
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [technicians, setTechnicians] = useState<ITechnician[]>([]);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);

  const [filters, setFilters] = useState<TechnicianFilterData>({
    type: 'ACD',
    position: '',
    excludePosition: 'HELPER,TBA',
    status: '',
    kycStatus: 'VERIFIED',
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchTechnician(page, rowsPerPage, searchQuery, sort, filters);
  }, [page, rowsPerPage, searchQuery, sort, filters]);

  const fetchTechnician = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
    filters?: TechnicianFilterData,
  ) => {
    try {
      setIsLoading(true);

      const response = await GETTechnicians({
        page,
        limit,
        searchQuery,
        sortOrder: sort,

        /**
         * Important:
         * page type API payload me hi pass karo.
         * Frontend par data filter mat karo,
         * warna pagination wrong ho jayega.
         */
        ...filters,
        type: 'ACD',
        excludePosition: 'HELPER,TBA',
        kycStatus: 'VERIFIED',
        workEligible: true,
      });

      setTechnicians(response?.data || []);

      /**
       * Important:
       * count backend ka use karo.
       * response.data.length use karoge to pagination wrong hoga.
       */
      setTotalTechnician(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
      setTechnicians([]);
      setTotalTechnician(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const refreshList = () => {
    fetchTechnician(page, rowsPerPage, searchQuery, sort, filters);
  };

  const handleFilterRefresh = (filterData: TechnicianFilterData) => {
    setFilters({
      ...filterData,
      type: 'ACD',
      excludePosition: 'HELPER,TBA',
      position: filterData.position === 'TBA' ? '' : filterData.position,
      kycStatus: 'VERIFIED',
    });

    setPage(1);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleSortRefresh = () => {
    setSort((prevSort) => (prevSort === 'ASC' ? 'DESC' : 'ASC'));
    setPage(1);
  };

  const handleAddModal = () => {
    setOpenAddModal(true);
  };

  const handleSaveRefresh = () => {
    refreshList();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="NVK Technician"
        createFilter={['SEARCH', 'ADD', 'FILTER']}
        addDrawerSize="lg"
        filterdrawerSize="sm"
        filterElement={
          <TechnicianFilter
            onFiltered={handleFilterRefresh}
            filters={filters}
            showType={false}
            showPosition={true}
            showStatus={true}
            showKycStatus={true}
            onResetFilter={() =>
              handleFilterRefresh({
                type: 'ACD',
                position: '',
                excludePosition: 'HELPER,TBA',
                status: '',
                kycStatus: 'VERIFIED',
              })
            }
          />
        }
        filterHeader={'Filter Technicians'}
        addHeader={'Add Technician'}
        addbuttontitle="Add Technician"
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onSaved={refreshList}
        onNavigate={handleAddModal}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {openAddModal && (
          <AddEditTechnicianModal
            open={openAddModal}
            onClose={() => setOpenAddModal(false)}
            action="ADNEW"
            onSaved={() => {
              handleSaveRefresh();
              setOpenAddModal(false);
            }}
          />
        )}

        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {technicians.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Verified Technicians Found
            </Typography>
          ) : (
            <ACD_technicianlist
              headerCells={headerTechnicianTable}
              technicians={technicians}
              isLoading={isLoading}
              onUpdate={refreshList}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalTechnician}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(ACD_technician);
